import {
  Intervention,
  InterventionCategory,
  PathwayIntervention,
  SubSector,
} from '@/types';
import { useCallback, useMemo } from 'react';
import { set, noop } from 'lodash/fp';
import {
  InPlaceInput,
  Select,
  Stack,
  LabelText,
  Text,
  MultiSelect,
} from '@kinesis/bungle';
import { StopTable } from '@/components/stop-table';
import { TagSelect } from '@/components/tag-select';
import { TagSetSelect } from '@/components/tag-set-select';
import { renderSubSector, renderDataType } from '@/data/labels';
import {
  interventionOptionsByCategory,
  interventionOptions,
  creatableInterventionOptionsByCategory,
  creatableInterventionOptions,
  targetableSubSectors,
} from '@/data/interventions';

const metricOptions = [
  { value: 'population', label: 'Population' },
  { value: 'dwellings', label: 'Dwellings' },
  { value: 'employment', label: 'Employment' },
];

const InterventionDetail = (props: InterventionDetailProps) => {
  const {
    category,
    autoFocus = false,
    allowTypeChange = false,
    intervention,
    onUpdate = noop,
    subSectors = targetableSubSectors,
  } = props;
  const optionsSubSectors = useMemo(
    () =>
      subSectors.map((v) => ({
        value: v,
        label: renderSubSector(v),
      })),
    [subSectors],
  );
  const options = allowTypeChange
    ? category
      ? creatableInterventionOptionsByCategory[category]
      : creatableInterventionOptions
    : category
      ? interventionOptionsByCategory[category]
      : interventionOptions;
  const setName = useCallback(
    (v: string) => onUpdate(set('name', v)),
    [onUpdate],
  );
  const setTag = useCallback(
    (v: string) => onUpdate(set('tag', v)),
    [onUpdate],
  );
  const setDescription = useCallback(
    (v: string) => onUpdate(set('description', v)),
    [onUpdate],
  );
  const setType = useCallback(
    (v: string) => onUpdate(set('type', v)),
    [onUpdate],
  );
  const setSectors = useCallback(
    (v: string[]) => onUpdate(set('sectors', v)),
    [onUpdate],
  );
  const setFactors = useCallback(
    (v: any) => onUpdate(set('factors', v)),
    [onUpdate],
  );
  const setReductions = useCallback(
    (v: any) => onUpdate(set('reductions', v)),
    [onUpdate],
  );
  const setIncreases = useCallback(
    (v: any) => onUpdate(set('increases', v)),
    [onUpdate],
  );
  const setData = useCallback(
    (v: string) => onUpdate(set('data', v)),
    [onUpdate],
  );
  return (
    <Stack space='medium'>
      <InPlaceInput
        variant='title'
        value={intervention.name}
        onChange={setName}
        placeholder='Intervention title'
        autoFocus={autoFocus}
      />
      <InPlaceInput
        variant='description'
        value={intervention.description}
        placeholder='Intervention description'
        onChange={setDescription}
      />
      {'tags' in intervention ? (
        // The new-model pathway intervention: an explicit tag set (which
        // evaluations it applies in) in place of the legacy single tag.
        <TagSetSelect
          value={intervention.tags}
          onChange={(v) => onUpdate(set('tags', v))}
        />
      ) : (
        <TagSelect value={intervention.tag} onChange={setTag} />
      )}
      <Select
        magnitude='large'
        options={options}
        value={intervention.type}
        onChange={setType}
        disabled={!allowTypeChange}
      />
      {intervention.type == 'assumption-growth' && (
        <>
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>
                {intervention.data
                  ? renderDataType(intervention.data)
                  : 'Metric'}{' '}
                growth
              </LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
          </Stack>
        </>
      )}
      {intervention.type == 'assumption' && (
        <>
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>
                {intervention.data
                  ? renderDataType(intervention.data)
                  : 'Metric'}{' '}
                projection
              </LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
          </Stack>
        </>
      )}

      {intervention.type == 'assumption-percentage' && (
        <>
          <Select
            magnitude='large'
            options={metricOptions}
            value={intervention.data}
            onChange={setData}
            placeholder='Select metric'
          />
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>
                {intervention.data
                  ? renderDataType(intervention.data)
                  : 'Metric'}{' '}
                percentage change
              </LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.increases}
              onChange={setIncreases}
              heading='% increase from baseline'
              unit='%'
            />
          </Stack>
        </>
      )}

      {intervention.type == 'assumption-derived-dwellings' && (
        <Stack space='medium'>
          <Text tone='secondary'>
            Dwellings will be derived from population changes using the current
            baseline ratio of population per dwelling for each sub-sector.
          </Text>
        </Stack>
      )}

      {intervention.type == 'grid-decarbonisation' && (
        <>
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>Grid decarbonsation</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              allYears
              stops={intervention.factors}
              heading={`Emissions Factor (kg\u00A0CO\u2082‑e)`}
              onChange={setFactors}
            />
          </Stack>
        </>
      )}
      {intervention.type == 'grid-decarbonisation-percentage' && (
        <>
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>Grid decarbonsation (percentage)</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              allYears
              stops={intervention.factors}
              heading={`% reduction in emissions factor`}
              onChange={setFactors}
            />
          </Stack>
        </>
      )}

      {intervention.type == 'building-efficiency' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>Building efficiency</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='% reduction in energy use'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'building-electrification' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>Building electrification</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Electrification of stationary gas use in existing buildings'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'new-building-standards' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Improved sustainability standards in new buildings'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'mode-shift-to-public-transport' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Proportion of car trips that are replaced by public transport'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'mode-shift-to-active-transport' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Proportion of car trips that are replaced by active transport'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'electric-vehicle-takeup' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Proportion of all cars that are electric'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'road-to-rail-freight-mode-shift' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Proportion of freight diverted from road to rail'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'rail-freight-efficiency' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='% improvement of rail efficiency'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'air-transport-efficiency' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='% improvement of air transport efficiency'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'waste-reduction' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Reduction in waste generation'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'waste-diversion-from-landfill' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Proportion of recyclable material that is recovered and diverted from landfill'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'waste-treatment' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Reduction in waste emissions'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'wastewater-treatment' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Reduction in wastewater emissions'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'industrial-processes' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Reduction in industrial process related emissions'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'product-use' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Reduction in product use related emissions'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'livestock' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Reduction in livestock related emissions'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'horticulture' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Reduction in horticulture related emissions'
              unit='%'
            />
          </Stack>
        </>
      )}
      {intervention.type == 'exotic-forest-change' && (
        <>
          <MultiSelect
            label='Sectors'
            options={optionsSubSectors}
            values={intervention.sectors}
            onChange={setSectors}
          />
          <Stack space='medium'>
            <Stack space='xsmall'>
              <LabelText>{'\u00A0'}</LabelText>
              <Text tone='secondary'>
                Missing values will be linearly interpolated.
              </Text>
            </Stack>
            <StopTable
              stops={intervention.reductions}
              onChange={setReductions}
              heading='Exotic forest change'
              unit='%'
            />
          </Stack>
        </>
      )}
    </Stack>
  );
};

export type InterventionDetailProps = {
  // Legacy interventions carry a single `tag`; new-model pathway
  // interventions carry the explicit `tags` set. The intent payload is the
  // same vocabulary either way.
  intervention: Intervention | PathwayIntervention;
  onUpdate?: (f: (x: any) => any) => void;
  category?: InterventionCategory;
  allowTypeChange?: boolean;
  autoFocus?: boolean;
  // The sub-sectors targeting offers — the new-model pages narrow this to
  // what the basis carries (selections are contextual). Defaults to the
  // whole modelling axis, the legacy behaviour.
  subSectors?: SubSector[];
};

export { InterventionDetail };
